import { IVehicleSearch, IServices } from "../interfaces/VehicleSearch";
import { vehicleType } from "./vehicleSegment";

// Single source of truth for building GA4 ecommerce `items[]` payloads.
// Everything here is defensive (optional chaining + fallbacks) so it can never
// throw and break the booking flow — analytics is best-effort.

export const GA4_CURRENCY = "EUR";

export interface GA4Item {
  item_id: string;
  item_name: string;
  item_brand?: string;
  item_variant?: string;
  item_category?: string;
  item_category2?: string;
  price?: number;
  quantity?: number;
  index?: number;
  item_list_id?: string;
  item_list_name?: string;
  [key: string]: string | number | boolean | undefined;
}

const round2 = (n: number): number => Number((Number(n) || 0).toFixed(2));

/** Prices are stored in cents across the API — convert to euros for GA4. */
const toEuros = (cents: number | null | undefined): number => round2((Number(cents) || 0) / 100);

export interface ServiceDef {
  name: string;
  category: string;
  pricingField: string;
}

export const SERVICE_CATALOG: Record<string, ServiceDef> = {
  "1028": { name: "Low Deductible", category: "insurance", pricingField: "lowDeductible" },
  "1053": { name: "Mid Deductible", category: "insurance", pricingField: "midDeductible" },
  "1030": { name: "High Deductible", category: "insurance", pricingField: "highDeductible" },
  "1026": { name: "Windshield Insurance", category: "insurance", pricingField: "windshieldInsurance" },
  "1038": { name: "Tire Insurance", category: "insurance", pricingField: "tireInsurance" },
  "1040": { name: "Cancellation Insurance", category: "insurance", pricingField: "cancellationInsurance" },
  "1023": { name: "Export", category: "extra", pricingField: "export" },
  "1027": { name: "Tow Hitch", category: "extra", pricingField: "towHitch" },
  "1041": { name: "Additional Driver", category: "extra", pricingField: "additionalDriver" },
  "1047": { name: "CO2 Compensation (125g)", category: "extra", pricingField: "hundredTwentyFiveGramsCOTwo" },
  "1048": { name: "CO2 Compensation (500g)", category: "extra", pricingField: "fiveHundredGramsCOTwo" },
  "1049": { name: "CO2 Compensation (700g)", category: "extra", pricingField: "sevenHundredGramsCOTwo" },
};

interface VehicleMetaInput {
  vehicleModelData?: { manufacturer?: string; name?: string; type?: string; dimensions?: { trunkVolume?: number } };
  tagCloud?: string[];
}

export const vehicleItemName = (v: VehicleMetaInput): string => {
  const brand = v?.vehicleModelData?.manufacturer ?? "";
  const model = v?.vehicleModelData?.name ?? "";
  const trunk = v?.vehicleModelData?.dimensions?.trunkVolume || 0;
  return `${brand} ${model}${trunk ? ` ${trunk} m3` : ""}`.trim();
};

interface VehicleItemOpts {
  noVat?: boolean;
  /** Override price (already in euros). When omitted, uses the vehicle total. */
  priceEuros?: number;
  quantity?: number;
  index?: number;
  listId?: string;
  listName?: string;
}

interface VehicleItemInput extends VehicleMetaInput {
  id?: string | number;
  pricingData?: { withCoupon?: { totalPrice?: number } | null; base?: { totalPrice?: number } };
  pricingDataNoVat?: { withCoupon?: { totalPrice?: number } | null; base?: { totalPrice?: number } };
}

export const buildVehicleItem = (v: VehicleItemInput, opts: VehicleItemOpts = {}): GA4Item => {
  const pricing = opts.noVat ? v?.pricingDataNoVat : v?.pricingData;
  const totalCents = pricing?.withCoupon?.totalPrice ?? pricing?.base?.totalPrice;
  const item: GA4Item = {
    item_id: String(v?.id ?? vehicleItemName(v)),
    item_name: vehicleItemName(v),
    item_brand: v?.vehicleModelData?.manufacturer ?? "",
    item_variant: v?.vehicleModelData?.name ?? "",
    item_category: vehicleType(v),
    price: opts.priceEuros != null ? round2(opts.priceEuros) : toEuros(totalCents),
    quantity: opts.quantity ?? 1,
  };
  if (opts.index != null) item.index = opts.index;
  if (opts.listId) item.item_list_id = opts.listId;
  if (opts.listName) item.item_list_name = opts.listName;
  return item;
};

interface SimpleItemOpts {
  id: string | number;
  name: string;
  priceEuros?: number;
  category?: string;
  index?: number;
  listId?: string;
  listName?: string;
}

/** Minimal GA4 item for places where only id/name/price are available (e.g. on-sale cards). */
export const buildSimpleItem = (o: SimpleItemOpts): GA4Item => {
  const item: GA4Item = {
    item_id: String(o.id),
    item_name: o.name,
    quantity: 1,
  };
  if (o.priceEuros != null) item.price = round2(o.priceEuros);
  if (o.category) item.item_category = o.category;
  if (o.index != null) item.index = o.index;
  if (o.listId) item.item_list_id = o.listId;
  if (o.listName) item.item_list_name = o.listName;
  return item;
};

type PriceBucket = Record<string, number | null | undefined>;

/**
 * GA4 items for every selected extra / insurance / mileage option.
 * Reads the clean `IServices` flags + per-service prices from pricingData,
 * mirroring the price math used by the purchase event (price/100 * days).
 */
export const buildServiceItems = (
  v: Partial<IVehicleSearch>,
  services: Partial<IServices> | undefined,
  reservationLength: number,
  noVat?: boolean
): GA4Item[] => {
  if (!services) return [];
  const pricing = noVat ? v?.pricingDataNoVat : v?.pricingData;
  const p: PriceBucket = (pricing?.withCoupon ?? pricing?.base ?? {}) as unknown as PriceBucket;
  const days = Number(reservationLength) || 1;
  const items: GA4Item[] = [];

  const add = (id: string, name: string, perDayCents: number | null | undefined, category: string) => {
    const price = round2(((Number(perDayCents) || 0) / 100) * days);
    if (price > 0) items.push({ item_id: id, item_name: name, price, quantity: 1, item_category: category });
  };

  // Mileage
  if (services.kmRange === 0) {
    add("unlimitedKilometers", "Unlimited Kilometers", p.unlimitedKilometers, "mileage");
  } else if (typeof services.kmRange === "number" && services.kmRange > 100) {
    const extraKm = Math.max(0, services.kmRange - 100);
    add(`${services.kmRange}km`, `${services.kmRange} km`, (Number(p.pricePerKilometer) || 0) * extraKm, "mileage");
  }

  const addSvc = (id: string) => {
    const def = SERVICE_CATALOG[id];
    if (def) add(id, def.name, p[def.pricingField], def.category);
  };

  // Deductibles (codes carried in the comma-separated `insurance` string)
  const insuranceIds = String(services.insurance || "").split(",").map(s => s.trim()).filter(Boolean);
  if (insuranceIds.includes("1028")) addSvc("1028");
  if (insuranceIds.includes("1053")) addSvc("1053");
  if (insuranceIds.includes("1030")) addSvc("1030");

  // Boolean-flag extras
  if (services.windshield) addSvc("1026");
  if (services.tireInsurance) addSvc("1038");
  if (services.cancellationInsurance) addSvc("1040");
  if (services.export) addSvc("1023");
  if (services.hook) addSvc("1027");
  if (services.additionalDriver) addSvc("1041");

  // CO2 compensation (single selected tier)
  if (services.co2compensation === "1047") addSvc("1047");
  if (services.co2compensation === "1048") addSvc("1048");
  if (services.co2compensation === "1049") addSvc("1049");

  return items;
};

/** Sum of item prices (euros), handy for `value` on cart/checkout events. */
export const itemsValue = (items: GA4Item[]): number =>
  round2(items.reduce((sum, it) => sum + (Number(it.price) || 0), 0));

export interface GA4Cart {
  items: GA4Item[];
  value: number;
}

/**
 * Full cart for add_to_cart / begin_checkout / add_payment_info: the vehicle
 * (at its base price) plus one item per selected extra. `pricingData.base.totalPrice`
 * is the VEHICLE base only (incl. 100km + default deductible), NOT the running
 * cart total — the selected extras are added on top, so value = base + extras.
 */
export const buildCart = (
  v: Partial<IVehicleSearch>,
  services: Partial<IServices> | undefined,
  reservationLength: number,
  noVat?: boolean
): GA4Cart => {
  const serviceItems = buildServiceItems(v, services, reservationLength, noVat);
  const vehicleItem = buildVehicleItem(v, { noVat });
  const value = round2((Number(vehicleItem.price) || 0) + itemsValue(serviceItems));
  return { items: [vehicleItem, ...serviceItems], value };
};

/** Human-readable payment_type for GA4 from the internal paymentMethod code. */
export const paymentTypeLabel = (method: number | undefined): string => {
  switch (Number(method)) {
    case 0: return "online-bank";
    case 1: return "card";
    case 2: return "invoice";
    case 3: return "quota";
    case 6: return "free";
    case 200: return "card_and_save";
    case 201: return "stored_card";
    case 2000: return "credit_card";
    default: return String(method);
  }
};
